/**
 * Toast — trả lời câu *"tôi bấm rồi, xong chưa?"*.
 *
 * Lynust nêu đúng triệu chứng này (09/09/2026): *"bấm rồi không biết đã xong chưa"*. Nguyên nhân
 * đo được: kết quả của một hành động được hiện bằng `<Bao>` **chèn vào giữa thân trang**. Trên
 * màn dài — Cài đặt, Cấu trúc prompt — chỗ đó thường **nằm ngoài tầm nhìn** ngay lúc bấm, nên
 * phản hồi có tồn tại mà người bấm không thấy.
 *
 * ⚠️ **Toast dành cho KẾT QUẢ MỘT HÀNH ĐỘNG, không dành cho trạng thái thường trực.**
 * "Đã duyệt bài" là toast. "3 job đang lỗi" thì không — nó vẫn đúng sau khi toast biến mất, nên
 * nó phải sống trên trang bằng `<Bao>`.
 *
 * Bốn điều chép từ `.claude/skills/emil-design-eng`:
 *
 * 1. **Transition, không phải keyframes.** Toast bị thêm liên tiếp; keyframes khởi động lại từ 0
 *    khi bị ngắt, transition thì đổi hướng mượt từ vị trí hiện tại.
 * 2. **Không bao giờ vào từ `scale(0)`.** Không có gì trong đời thật hiện ra từ hư vô — vào từ
 *    `0.98` cộng `opacity`.
 * 3. **Ra nhanh hơn vào** (140ms so với 220ms). Người dùng đã đọc xong rồi; chờ thêm là thuế.
 * 4. **Xử lý ca biên trong im lặng**: tab bị ẩn thì **dừng đồng hồ**. Không có nó, mở lại tab sau
 *    năm phút là toast đã tự tắt và hành động vừa rồi trông như chưa từng xảy ra.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";

import { BtDong } from "./bieu-tuong.tsx";

type MucToast = "on" | "loi" | "thuong";

interface Toast {
  readonly id: number;
  readonly muc: MucToast;
  readonly tieuDe: string;
  readonly than?: ReactNode;
  /** Còn bao nhiêu mili giây nữa thì tự tắt. Đếm ngược **chỉ khi tab đang hiện**. */
  conLai: number;
  /** Đang trong lượt ra — giữ trong DOM thêm `NHIP_RA` để transition chạy hết. */
  dangRa?: boolean;
}

/** Lỗi ở lâu hơn: người đọc cần thời gian đọc một thông điệp lỗi, không chỉ liếc qua. */
const SONG_MS: Record<MucToast, number> = { on: 4_000, thuong: 4_000, loi: 9_000 };

/** Khớp `--nhip-ra` trong `index.css`. Lệch hai bên là toast biến mất giữa lượt trượt. */
const NHIP_RA = 140;

/**
 * Nhiều hơn ba cái chồng lên nhau thì cái dưới cùng không đọc được nữa, và trên điện thoại chúng
 * ăn hết nửa màn hình. Cái cũ nhất bị đẩy ra khi cái thứ tư tới.
 */
const TOI_DA = 3;

interface KhoToast {
  bao: (muc: MucToast, tieuDe: string, than?: ReactNode) => void;
  on: (tieuDe: string, than?: ReactNode) => void;
  loi: (tieuDe: string, than?: ReactNode) => void;
}

const Ngu = createContext<KhoToast | null>(null);

/**
 * Gọi ở bất cứ đâu dưới `<NhaToast>`.
 *
 * Trả về một kho **không đổi giữa các lần render**, nên đặt nó vào mảng phụ thuộc của `useEffect`
 * hay `useCallback` là an toàn — thiếu điều đó thì mỗi lần cha render lại là một vòng lặp.
 */
export function useToast(): KhoToast {
  const kho = useContext(Ngu);
  if (kho === null) {
    throw new Error("useToast phải nằm trong <NhaToast>. Bọc nó ở App.tsx.");
  }
  return kho;
}

export function NhaToast({ children }: { children: ReactNode }) {
  const [ds, datDs] = useState<Toast[]>([]);
  const soTiepTheo = useRef(1);

  const dong = useCallback((id: number) => {
    // Đánh dấu `dangRa` trước, xoá sau `NHIP_RA`: xoá thẳng khỏi mảng là bỏ qua lượt trượt ra và
    // toast **biến mất đột ngột** — đúng thứ Emil gọi là "feels broken".
    datDs((cu) => cu.map((t) => (t.id === id ? { ...t, dangRa: true } : t)));
    window.setTimeout(() => datDs((cu) => cu.filter((t) => t.id !== id)), NHIP_RA);
  }, []);

  const kho = useMemo<KhoToast>(() => {
    const bao = (muc: MucToast, tieuDe: string, than?: ReactNode) => {
      const id = soTiepTheo.current++;
      datDs((cu) => [...cu.filter((t) => !t.dangRa).slice(-(TOI_DA - 1)), { id, muc, tieuDe, than, conLai: SONG_MS[muc] }]);
    };
    return {
      bao,
      on: (tieuDe, than) => bao("on", tieuDe, than),
      loi: (tieuDe, than) => bao("loi", tieuDe, than),
    };
  }, []);

  /**
   * Một đồng hồ duy nhất cho cả chồng, thay vì một `setTimeout` mỗi toast.
   *
   * Nhờ vậy **dừng khi tab bị ẩn** là một câu `if`, không phải việc huỷ và dựng lại từng hẹn giờ.
   * `document.hidden` được đọc **tại mỗi nhịp**, nên nó đúng cả khi người dùng đổi tab giữa chừng.
   */
  useEffect(() => {
    if (ds.length === 0) return;
    const BUOC = 100;
    const dongHo = window.setInterval(() => {
      if (document.hidden) return;
      datDs((cu) => {
        const moi = cu.map((t) => (t.dangRa ? t : { ...t, conLai: t.conLai - BUOC }));
        for (const t of moi) if (!t.dangRa && t.conLai <= 0) dong(t.id);
        return moi;
      });
    }, BUOC);
    return () => window.clearInterval(dongHo);
  }, [ds.length, dong]);

  return (
    <Ngu.Provider value={kho}>
      {children}
      {/*
        Trên điện thoại: dính đáy, tràn ngang — cùng phía với thanh hành động, nên phản hồi hiện
        ra **ngay chỗ ngón tay vừa rời đi**. Trên desktop: góc phải dưới.
        `pointer-events-none` ở lớp bọc để chồng toast không nuốt cú bấm vào trang phía sau;
        từng toast tự bật lại.
      */}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:items-end sm:p-0"
        // `polite`: đọc sau khi người dùng dừng thao tác, không cắt ngang. Toast lỗi tự mang
        // `role="alert"` bên trong nên nó vẫn được đọc ngay.
        aria-live="polite"
      >
        {ds.map((t) => (
          <MotToast key={t.id} t={t} dong={() => dong(t.id)} />
        ))}
      </div>
    </Ngu.Provider>
  );
}

function MotToast({ t, dong }: { t: Toast; dong: () => void }) {
  /**
   * `hien` bật ở lần render **sau** lần đầu, để trình duyệt kịp vẽ trạng thái đầu rồi mới chuyển.
   * Đặt thẳng trạng thái cuối ngay từ đầu thì không có transition nào chạy cả — toast bụp ra.
   */
  const [hien, datHien] = useState(false);
  useEffect(() => {
    const k = requestAnimationFrame(() => datHien(true));
    return () => cancelAnimationFrame(k);
  }, []);

  const mau = {
    on: "border-on-200 bg-on-50 text-on-700",
    loi: "border-loi-200 bg-loi-50 text-loi-700",
    thuong: "border-muc-300 bg-muc-0 text-muc-700",
  }[t.muc];

  const dangHien = hien && !t.dangRa;

  return (
    <div
      role={t.muc === "loi" ? "alert" : "status"}
      className={clsx(
        "pointer-events-auto flex w-full max-w-[26rem] min-w-0 items-start gap-2 rounded-card border p-3 shadow-noi",
        // Nêu ĐÚNG thuộc tính. `transition` trần chạy cả `background-color`, `border`, `color` —
        // và mỗi cái là một lần tính lại không ai yêu cầu.
        "transition-[opacity,transform] duration-[var(--nhip-vao)] ease-[var(--ease-ra)]",
        // Vào từ 0.98 chứ không phải 0: không có gì trong đời thật hiện ra từ hư vô.
        dangHien ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.98] opacity-0",
        t.dangRa && "duration-[var(--nhip-ra)]",
        mau,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{t.tieuDe}</p>
        {t.than !== undefined && <div className="mt-0.5 text-xs opacity-90">{t.than}</div>}
      </div>
      <button
        onClick={dong}
        aria-label="Đóng thông báo"
        // `bam-tu-do`: không ép cao 44px cho một nút đóng 28px nằm trong toast — nó sẽ kéo cả
        // toast cao lên. Vùng chạm vẫn đủ nhờ `p-1.5` cộng icon 16px.
        className="bam-tu-do -m-1 shrink-0 rounded-btn p-1.5 opacity-60 transition-opacity duration-100 hover:opacity-100"
      >
        <BtDong className="h-4 w-4" />
      </button>
    </div>
  );
}
